import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  LinearProgress,
  Radio,
  RadioGroup,
  Stack,
  Typography,
} from '@mui/material';
import UploadCard from './UploadCard.jsx';

const PRIOR_GM_EXPORT_MODE = 'latest_gm_export';

const emptyUpload = { rows: [], fileName: null, file: null, seeded: false, serverParsed: false };

const NewRoundDialog = ({
  open,
  nextRoundNumber = 1,
  program = 'website',
  initialSourceMode = 'upload',
  mockMode = false,
  mockSeedData = null,
  creating = false,
  onClose,
  onCreate,
}) => {
  const showComparisonSource = nextRoundNumber > 1;
  const comparisonLabel = String(program || '').toLowerCase() === 'trade'
    ? 'Trade comparison source'
    : 'CPQ/DI comparison source';
  const [gm, setGm] = useState(emptyUpload);
  const [cpq, setCpq] = useState(emptyUpload);
  const [sourceMode, setSourceMode] = useState(initialSourceMode);

  useEffect(() => {
    if (!open) return;
    setGm(emptyUpload);
    setCpq(emptyUpload);
    setSourceMode(initialSourceMode || 'upload');
  }, [initialSourceMode, open]);

  const effectiveSourceMode = showComparisonSource ? sourceMode : 'upload';
  const canSubmit = useMemo(() => {
    if (mockMode) return true;
    return Boolean(gm.file && (effectiveSourceMode === PRIOR_GM_EXPORT_MODE || cpq.file));
  }, [cpq.file, effectiveSourceMode, gm.file, mockMode]);

  const handleSubmit = () => {
    if (!canSubmit || creating) return;
    onCreate?.({
      gm: gm.file ? gm : {
        rows: mockSeedData?.gmRows || [],
        fileName: null,
        file: null,
        seeded: Boolean(mockMode),
        serverParsed: false,
      },
      cpq: effectiveSourceMode === PRIOR_GM_EXPORT_MODE
        ? emptyUpload
        : (cpq.file ? cpq : {
          rows: mockSeedData?.cpqRows || [],
          fileName: null,
          file: null,
          seeded: Boolean(mockMode),
          serverParsed: false,
        }),
      cpqSourceMode: effectiveSourceMode,
    });
  };

  return (
    <Dialog open={open} onClose={creating ? undefined : onClose} maxWidth="lg" fullWidth>
      <DialogTitle>
        <Stack spacing={0.25}>
          <Typography variant="h6" sx={{ fontWeight: 800 }}>
            New reconciliation round
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Round {nextRoundNumber}
          </Typography>
        </Stack>
      </DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2}>
          {creating ? (
            <Alert severity="info" variant="outlined">
              Round upload is still processing. Keep this page open; creating another round is disabled until it finishes.
              <LinearProgress sx={{ mt: 1 }} />
            </Alert>
          ) : null}

          {showComparisonSource ? (
            <Box>
              <Typography variant="subtitle2" sx={{ fontWeight: 800, mb: 0.5 }}>
                {comparisonLabel}
              </Typography>
              <RadioGroup
                row
                value={sourceMode}
                onChange={(event) => setSourceMode(event.target.value)}
                name="billing-new-round-cpq-source-mode"
              >
                <FormControlLabel value="upload" control={<Radio size="small" />} label="Upload CPQ/DI file" />
                <FormControlLabel value={PRIOR_GM_EXPORT_MODE} control={<Radio size="small" />} label="Use prior GM export" />
              </RadioGroup>
            </Box>
          ) : null}

          <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
            <UploadCard
              title="GM Billing File"
              subtitle="GM billing feed CSV, TXT, or workbook for this round"
              source="gm"
              currentFile={gm.fileName}
              rowCount={gm.rows.length}
              serverParsed={gm.serverParsed}
              seeded={gm.seeded}
              sampleAvailable={mockMode}
              onParsed={(payload) => setGm({ ...payload, seeded: false, serverParsed: Boolean(payload.serverParsed) })}
              onClear={() => setGm(emptyUpload)}
            />
            {effectiveSourceMode === PRIOR_GM_EXPORT_MODE ? (
              <Alert severity="info" variant="outlined" sx={{ flex: 1, alignItems: 'center' }}>
                The API will use the latest saved GM correction export for this program and month as the DI baseline.
              </Alert>
            ) : (
              <UploadCard
                title="DI CPQ Extract"
                subtitle="Dealer Inspire CPQ billing CSV, TXT, or workbook"
                source="cpq"
                currentFile={cpq.fileName}
                rowCount={cpq.rows.length}
                serverParsed={cpq.serverParsed}
                seeded={cpq.seeded}
                sampleAvailable={mockMode}
                onParsed={(payload) => setCpq({ ...payload, seeded: false, serverParsed: Boolean(payload.serverParsed) })}
                onClear={() => setCpq(emptyUpload)}
              />
            )}
          </Stack>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={creating}>Cancel</Button>
        <Button variant="contained" onClick={handleSubmit} disabled={!canSubmit || creating}>
          {creating ? 'Creating...' : 'Create round'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default NewRoundDialog;
